'use client'

import { useTheme } from '@mui/material/styles'
import { Box } from '@mui/material'

// Types
import type { MarqueeProps } from '@/types/components/atoms/animation'

// Endless, slowly gliding row that moves with the reading direction and pauses on hover
const MarqueeAtom = (props: MarqueeProps) => {
	// Props
	const { items, itemWidth, gap = { xs: 2, md: 3 }, secondsPerItem = 8 } = props

	// Variables
	const theme = useTheme()

	return (
		// The track runs left to right in both languages (dir attribute, which the RTL plugin leaves alone); each item keeps the page direction
		<Box
			dir="ltr"
			sx={{
				overflow: 'hidden',
				// Room for hover lifts and shadows
				py: 1.5,
				maskImage: 'linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)',
				WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)',
				'&:hover .marquee-track': { animationPlayState: 'paused' },
				// The offset sits in a variable so the RTL style plugin leaves it alone
				'@keyframes marqueeGlide': { from: { transform: 'translateX(0)' }, to: { transform: 'translateX(var(--glide-end))' } }
			}}
		>
			{/* Items are doubled so the loop has no visible seam */}
			<Box
				className="marquee-track"
				sx={{
					display: 'flex',
					alignItems: 'stretch',
					width: 'max-content',
					'--glide-end': '-50%',
					animation: `marqueeGlide ${items.length * secondsPerItem}s linear infinite`,
					// Glide with the reading direction; must come after the shorthand above
					animationDirection: theme.direction === 'rtl' ? 'reverse' : 'normal',
					'@media (prefers-reduced-motion: reduce)': { animation: 'none' }
				}}
			>
				{[...items, ...items].map((item, index) => (
					<Box key={index} aria-hidden={index >= items.length} inert={index >= items.length} dir={theme.direction} sx={{ width: itemWidth, flexShrink: 0, pr: gap }}>
						{item}
					</Box>
				))}
			</Box>
		</Box>
	)
}

export default MarqueeAtom
